import {Component} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {
    BucketName,
    KnowledgeDocument,
    KnowledgeDocumentsApiService,
    KnowledgeDocumentType,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {LocalizationService, RouterService, rxSpinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {Observable} from 'rxjs';
import {AttachmentForm, createAttachmentRequest} from '../../../../../components/files/file/file.component';
import {KB_MIME_TYPES} from '../../../../../domain/constants';
import {KNOWLEDGE_DOCUMENT_ID} from '../../../../../domain/query-params.keys';
import {Path} from '../../../../../path';
import {KnowledgeBaseTab} from '../../../knowledge-base.component';

@Component({
    selector: 'af-create-edit-knowledge-base-document',
    standalone: false,
    templateUrl: './create-edit-knowledge-base-document.component.html',
})
export class CreateEditKnowledgeBaseDocumentComponent {
    ReferenceBookType = ReferenceBookType;
    BucketName = BucketName;
    KB_MIME_TYPES = KB_MIME_TYPES;

    documentId = this.activatedRoute.snapshot.paramMap.get(KNOWLEDGE_DOCUMENT_ID);
    isEdit = !!this.documentId;

    form: FormGroup<CreateEditKnowledgeBaseDocumentForm>;
    tabPathToTab: Record<string, KnowledgeBaseTab> = {[Path.DOCUMENTS]: 'tabDocuments'};
    response: KnowledgeDocument;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private knowledgeDocumentsApiService: KnowledgeDocumentsApiService,
        private snackbarService: SnackbarService,
        private routerService: RouterService,
        private localizationService: LocalizationService,
    ) {
        if (this.documentId) {
            this.knowledgeDocumentsApiService.getKnowledgeDocument(this.documentId)
                .pipe(rxSpinner('initial'))
                .subscribe(response => {
                    this.response = response;
                    this.buildForm(this.response);
                });
        } else {
            this.buildForm();
        }
    }

    get isDocumentType(): boolean {
        return this.form.controls.type.value === KnowledgeDocumentType.DOCUMENT;
    }

    get isLinkType(): boolean {
        return [KnowledgeDocumentType.LINK, KnowledgeDocumentType.LINK_LAW].includes(this.form.controls.type.value);
    }

    breadcrumbCustomizer = (path: string): string => {
        if (path === this.documentId) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#EditDocument', {id: this.response?.name});
        }
        return path;
    };

    save(): void {
        if (this.form.invalid) {
            return;
        }
        const attachment = this.isDocumentType ? this.form.controls.attachment?.value : undefined;
        if (attachment) {
            attachment.fileType = 'KNOWLEDGE_BASE_DOCUMENT';
        }
        const requestBody = <KnowledgeDocument>{
            type: this.form.controls.type.value,
            name: this.form.controls.name.value,
            link: this.isLinkType ? this.form.controls.link.value : undefined,
            attachedDocument: this.isDocumentType ? createAttachmentRequest(attachment) : undefined,
        };
        let request$: Observable<KnowledgeDocument>;
        if (this.isEdit) {
            request$ = this.knowledgeDocumentsApiService.updateKnowledgeDocument(this.documentId, requestBody);
        } else {
            request$ = this.knowledgeDocumentsApiService.createKnowledgeDocument(requestBody);
        }
        request$
            .pipe(rxSpinner('save'))
            .subscribe(() => {
                this.snackbarService.showSnackBar(this.isEdit ? 'i18n_af_snackBarDocumentUpdated' : 'i18n_af_snackBarDocumentCreated');
                this.navigateBack();
            });
    }

    navigateBack(): void {
        this.routerService.navigateToTab<KnowledgeBaseTab>(Path.KNOWLEDGE_BASE, 'tabDocuments');
    }

    private buildForm(document?: KnowledgeDocument): void {
        this.form = this.formBuilder.group({
            type: new FormControl(document?.type, Validators.required),
            name: new FormControl(document?.name ?? '', [Validators.required, Validators.maxLength(150)]),
            link: new FormControl(document?.link),
            attachment: this.formBuilder.group({}),
        });
        this.form.controls.type.valueChanges.subscribe(type => this.onTypeChange(type));
        if (document?.type) {
            this.onTypeChange(document.type);
        }
    }

    private onTypeChange(type: KnowledgeDocumentType | null): void {
        const linkControl = this.form.controls.link;
        if (!type || type === KnowledgeDocumentType.DOCUMENT) {
            linkControl.clearValidators();
        } else {
            linkControl.setValidators([Validators.required, Validators.pattern(/^https?:\/\/.+/)]);
        }
        linkControl.updateValueAndValidity();
    }

}

interface CreateEditKnowledgeBaseDocumentForm {
    type: FormControl<KnowledgeDocumentType>;
    name: FormControl<string>;
    link: FormControl<string>;
    attachment: FormGroup<AttachmentForm>
}
